import React, { useRef, useState, useEffect, useCallback } from 'react';
import { omit } from 'lodash';
import NavigationSteps from '../../styled/navigation-steps';
import {
  Container,
  ScrollContainer,
  ScrollButton,
  StepsWrapper,
} from '../../styled/scrollable-navigation-steps';

const ScrollableNavigationSteps = props => {
  const { current, children, ...rest } = props;

  const containerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Sanitize children to avoid forwarding app-specific props to Ant Steps/Step DOM
  const sanitizedChildren = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;
    // remove props that should not be forwarded to DOM elements
    const filteredProps = omit(child.props, [
      'selectedForm',
      'showSubmitButton',
    ]);
    return React.cloneElement(child, filteredProps);
  });

  // Only forward recognized Ant Steps props to avoid passing custom props
  // (like `showSubmitButton`) down to the DOM.
  const stepsProps = {
    current,
    progressDot: rest.progressDot,
    direction: rest.direction,
    size: rest.size,
    status: rest.status,
    labelPlacement: rest.labelPlacement,
    onChange: rest.onChange,
  };

  const getActiveIndexFromDOM = container => {
    const items = container.querySelectorAll('.ant-steps-item');
    if (!items || items.length === 0) return -1;
    const activeEl = Array.from(items).find(i =>
      i.classList.contains('ant-steps-item-active')
    );
    if (activeEl) return Array.from(items).indexOf(activeEl);
    return -1;
  };

  // Fix 1: childCount is a dep so updateLayout fires whenever visible tabs
  // change count (hiding/showing a tab changes scrollWidth but NOT clientWidth,
  // so ResizeObserver alone would never detect it).
  const useFlexibleHorizontalScroll = (minItemWidth = 200, gap = 16, childCount = 0) => {
    const [hasOverflow, setHasOverflow] = useState(false);
    const [needToRecalculate, setNeedToRecalculate] = useState(false);
    const [availableWidth, setAvailableWidth] = useState(0);

    const updateLayout = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      // Check if there is overflow
      const hasHorizontalOverflow =
        container.scrollWidth > container.clientWidth;
      setHasOverflow(hasHorizontalOverflow);

      // If no overflow, compute even distribution of available width.
      if (!hasHorizontalOverflow) {
        const childEls = Array.from(container.children);
        if (childEls.length === 0) return;

        // Get children width
        const totalChildrenWidth = childEls.reduce((sum, child) => {
          return sum + child.getBoundingClientRect().width;
        }, 0);

        const containerWidth = container.clientWidth;
        const remainingSpace =
          containerWidth - totalChildrenWidth - gap * (childEls.length - 1);

        if (remainingSpace > 0) {
          setNeedToRecalculate(true);
          setAvailableWidth(containerWidth);
        }
      }
    }, [gap]);

    useEffect(() => {
      updateLayout();

      window.addEventListener('resize', updateLayout);

      const observer = new ResizeObserver(() => updateLayout());
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        window.removeEventListener('resize', updateLayout);
        observer.disconnect();
      };
    }, [updateLayout]);

    // Fix 1 (cont.): re-run layout when the number of rendered steps changes
    // (a tab was hidden or revealed by conditional logic).
    useEffect(() => {
      updateLayout();
    }, [childCount, updateLayout]);

    return {
      hasOverflow,
      needToRecalculate,
      availableWidth,
    };
  };

  const checkScrollBounds = () => {
    const container = containerRef.current;
    if (!container) return;

    const atStart = Math.abs(container.scrollLeft) < 1;
    const atEnd =
      Math.abs(
        container.scrollLeft - (container.scrollWidth - container.clientWidth)
      ) < 1;

    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
  };

  // to check scroll boundaries
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollBounds);
    window.addEventListener('resize', checkScrollBounds);

    checkScrollBounds();

    return () => {
      container.removeEventListener('scroll', checkScrollBounds);
      window.removeEventListener('resize', checkScrollBounds);
    };
  }, []);

  const scrollToStep = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const items = container.querySelectorAll('.ant-steps-item');
    if (items.length === 0) {
      requestAnimationFrame(scrollToStep);
      return;
    }

    let targetIndex = -1;
    if (typeof current !== 'undefined') {
      targetIndex = Number(current);
    } else {
      const domIndex = getActiveIndexFromDOM(container);
      targetIndex = domIndex;
    }

    if (targetIndex < 0 || targetIndex >= items.length) return;
    const activeStep = items[targetIndex];
    if (!activeStep) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeStep.getBoundingClientRect();
    const targetScrollLeft =
      container.scrollLeft +
      activeRect.left -
      containerRect.left -
      containerRect.width / 2 +
      activeRect.width / 2;
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    } else {
      container.scrollLeft = targetScrollLeft;
    }
  }, [current]);

  useEffect(() => {
    scrollToStep();
  }, [current, scrollToStep]);

  const scrollLeftHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (container) {
      const scrollAmount = 200;
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          left: Math.max(0, container.scrollLeft - scrollAmount),
          behavior: 'smooth',
        });
      } else {
        container.scrollLeft = Math.max(0, container.scrollLeft - scrollAmount);
      }
    }
  };

  const scrollRightHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (container) {
      const scrollAmount = 200;
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          left: Math.min(maxScroll, container.scrollLeft + scrollAmount),
          behavior: 'smooth',
        });
      } else {
        container.scrollLeft = Math.min(
          maxScroll,
          container.scrollLeft + scrollAmount
        );
      }
    }
  };

  const recalculateCssProps = (
    needToRecalculate,
    availableWidth,
    childCount
  ) => {
    if (!needToRecalculate || childCount <= 1) return { $needToRecalculate: false };
    const itemContentWidth = Math.round(availableWidth / childCount);
    const marginLeft = Math.round(itemContentWidth / 2);
    return {
      $needToRecalculate: true,
      $itemContentWidth: itemContentWidth - 16, // 16 is the gap between items
      $itemTailMarginLeft: marginLeft,
      $itemIconMarginLeft: Math.round(marginLeft - marginLeft * 0.05),
      $maxWidth: Math.round(itemContentWidth * 1.6),
    };
  };

  const childCount = sanitizedChildren ? sanitizedChildren.length : 0;
  const {
    hasOverflow,
    needToRecalculate,
    availableWidth,
  } = useFlexibleHorizontalScroll(200, 16, childCount);

  const cssProps = recalculateCssProps(
    needToRecalculate,
    availableWidth,
    childCount
  );
  return (
    <Container>
      {hasOverflow && (
        <ScrollButton
          type="button"
          onClick={scrollLeftHandler}
          disabled={!canScrollLeft}
          data-testid="scroll-left-button"
        >
          <svg viewBox="0 0 24 24">
            <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
          </svg>
        </ScrollButton>
      )}
      <ScrollContainer innerRef={containerRef} data-testid="scroll-container">
        <StepsWrapper>
          <NavigationSteps
            {...stepsProps}
            {...cssProps}
            data-testid="navigation-steps"
          >
            {sanitizedChildren}
          </NavigationSteps>
        </StepsWrapper>
      </ScrollContainer>
      {hasOverflow && (
        <ScrollButton
          type="button"
          onClick={scrollRightHandler}
          disabled={!canScrollRight}
          data-testid="scroll-right-button"
        >
          <svg viewBox="0 0 24 24">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
          </svg>
        </ScrollButton>
      )}
    </Container>
  );
};

export default ScrollableNavigationSteps;
